import React from "react";
interface FormFieldProps {
  label?: string;
  name: string;
  value?: string | boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | null;
  type?: string;
  placeholder?: string;
  enableCopyPaste?: boolean;
  showErrorMessage?: boolean;
  disabled?: boolean;
  className?: string;
  checked?: boolean;
}

export const FormField: React.FC<FormFieldProps> = ({
  label = "",
  name,
  value,
  onChange,
  onBlur,
  error,
  type = "text",
  placeholder,
  enableCopyPaste = true,
  showErrorMessage = true,
  disabled = false,
  className = "",
  checked,
}) => {
  //prevent copy-paste and drag-drop if disabled
  const handlePrevent = (
    e:
      | React.ClipboardEvent<HTMLInputElement>
      | React.DragEvent<HTMLInputElement>
  ) => {
    if (!enableCopyPaste) e.preventDefault();
  };

  //sanitize input to prevent <script>
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Basic sanitization: escape angle brackets to neutralize HTML/script injection.
    // This uses a simple, linear-time replace and avoids ReDoS risks from complex regexes.
    const sanitizedValue = inputValue.replaceAll(/[<>]/g, (c) => (c === '<' ? '&lt;' : '&gt;'));
    e.target.value = sanitizedValue;
    onChange(e);
  };

  return (
    <div
      className={`form-field ${error ? "form-field--error" : ""} form-field--responsive ${className}`}
    >
      {label && (
        <label htmlFor={name} className="form-field__label">
          {label}
        </label>
      )}

      {type === "checkbox" ? (
        <input
          id={name}
          name={name}
          type={type}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`form-field__input ${error ? "form-field__input--error" : ""}`}
        />
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value as string}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onBlur={onBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`form-field__input ${error ? "form-field__input--error" : ""}`}
          onCopy={handlePrevent}
          onPaste={handlePrevent}
          onCut={handlePrevent}
          onDragStart={handlePrevent}
          onDrop={handlePrevent}
          autoComplete="off"
        />
      )}

      {error && showErrorMessage && (
        <div
          id={`${name}-error`}
          className="form-field__error"
          role="alert"
        >
          {error}
        </div>
      )}
    </div>
  );
};
